import * as React from 'react';
import cx from 'classnames';
import { tokens } from '@fluentui/react-components';

type Align = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
type Justify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
type AlignContent = 'start' | 'end' | 'center' | 'stretch' | 'between' | 'around';

type CSSValue = string | number;

export type StackProps = {
    children: React.ReactNode;
    className?: string;
    direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
    align?: Align;
    justify?: Justify;
    wrap?: 'nowrap' | 'wrap' | 'wrap-reverse';
    inline?: boolean;
    alignContent?: AlignContent;
    grow?: number;
    shrink?: number;
    basis?: CSSValue;
    order?: number;
    alignSelf?: Align | 'auto';
    gap?: CSSValue;
    padding?: CSSValue | [CSSValue, CSSValue];
    margin?: CSSValue | [CSSValue, CSSValue];
    paddingTop?: CSSValue;
    paddingRight?: CSSValue;
    paddingBottom?: CSSValue;
    paddingLeft?: CSSValue;
    marginTop?: CSSValue;
    marginRight?: CSSValue;
    marginBottom?: CSSValue;
    marginLeft?: CSSValue;
    width?: CSSValue;
    height?: CSSValue;
    minWidth?: CSSValue;
    maxWidth?: CSSValue;
    minHeight?: CSSValue;
    maxHeight?: CSSValue;
};

const alignMap: Record<Align, string> = {
    start: 'flex-start',
    end: 'flex-end',
    center: 'center',
    stretch: 'stretch',
    baseline: 'baseline',
};

const justifyMap: Record<Justify, string> = {
    start: 'flex-start',
    end: 'flex-end',
    center: 'center',
    between: 'space-between',
    around: 'space-around',
    evenly: 'space-evenly',
};

const alignContentMap: Record<AlignContent, string> = {
    start: 'flex-start',
    end: 'flex-end',
    center: 'center',
    stretch: 'stretch',
    between: 'space-between',
    around: 'space-around',
};

/**
 * Flex box from layout props. It forwards ONLY the props declared above — no data attributes, no
 * aria, no DOM handlers — so a test marker, an accessible name or an event listener needs a plain
 * element around it.
 */
export const Stack = React.forwardRef<HTMLDivElement, StackProps>(
    (
        {
            children,
            className,
            direction = 'row',
            align = 'stretch',
            justify = 'start',
            wrap = 'nowrap',
            inline = false,
            alignContent = 'stretch',
            grow,
            shrink,
            basis,
            order,
            alignSelf = 'auto',
            gap = tokens.spacingHorizontalNone,
            padding,
            margin,
            paddingTop,
            paddingRight,
            paddingBottom,
            paddingLeft,
            marginTop,
            marginRight,
            marginBottom,
            marginLeft,
            width,
            height,
            minWidth,
            maxWidth,
            minHeight,
            maxHeight,
        },
        ref,
    ) => {
        const dynamicStyles: React.CSSProperties = {
            display: inline ? 'inline-flex' : 'flex',
            flexDirection: direction,
            alignItems: align ? alignMap[align] : undefined,
            justifyContent: justify ? justifyMap[justify] : undefined,
            flexWrap: wrap,
            alignContent: alignContent ? alignContentMap[alignContent] : undefined,
            flexGrow: grow,
            flexShrink: shrink,
            flexBasis: basis,
            order,
            alignSelf: alignSelf === 'auto' ? 'auto' : alignMap[alignSelf as Align],
            gap: typeof gap === 'number' ? `${gap}px` : gap,
            ...(padding !== undefined
                ? Array.isArray(padding)
                    ? { padding: `${padding[0]} ${padding[1]}` }
                    : { padding: typeof padding === 'number' ? `${padding}px` : padding }
                : {}),
            ...(margin !== undefined
                ? Array.isArray(margin)
                    ? { margin: `${margin[0]} ${margin[1]}` }
                    : { margin: typeof margin === 'number' ? `${margin}px` : margin }
                : {}),
            ...(paddingTop !== undefined && {
                paddingTop: typeof paddingTop === 'number' ? `${paddingTop}px` : paddingTop,
            }),
            ...(paddingRight !== undefined && {
                paddingRight: typeof paddingRight === 'number' ? `${paddingRight}px` : paddingRight,
            }),
            ...(paddingBottom !== undefined && {
                paddingBottom: typeof paddingBottom === 'number' ? `${paddingBottom}px` : paddingBottom,
            }),
            ...(paddingLeft !== undefined && {
                paddingLeft: typeof paddingLeft === 'number' ? `${paddingLeft}px` : paddingLeft,
            }),
            ...(marginTop !== undefined && { marginTop: typeof marginTop === 'number' ? `${marginTop}px` : marginTop }),
            ...(marginRight !== undefined && {
                marginRight: typeof marginRight === 'number' ? `${marginRight}px` : marginRight,
            }),
            ...(marginBottom !== undefined && {
                marginBottom: typeof marginBottom === 'number' ? `${marginBottom}px` : marginBottom,
            }),
            ...(marginLeft !== undefined && {
                marginLeft: typeof marginLeft === 'number' ? `${marginLeft}px` : marginLeft,
            }),
            ...(width !== undefined && { width: typeof width === 'number' ? `${width}px` : width }),
            ...(height !== undefined && { height: typeof height === 'number' ? `${height}px` : height }),
            ...(minWidth !== undefined && { minWidth: typeof minWidth === 'number' ? `${minWidth}px` : minWidth }),
            ...(maxWidth !== undefined && { maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth }),
            ...(minHeight !== undefined && { minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight }),
            ...(maxHeight !== undefined && { maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight }),
        };

        return (
            <div ref={ref} className={cx(className)} style={dynamicStyles}>
                {children}
            </div>
        );
    },
);

Stack.displayName = 'Stack';
